import { useNodeId } from "@xyflow/react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Typeahead, type Key } from "@zapier/design-system-beta";
import { Loadable } from "jotai/vanilla/utils/loadable";
import { Icon, Text } from "@zapier/design-system";
import {
  createZapTimeSpentFromTimeSavedAtom,
  newAssetIdentifierAtom,
  takeSnapshotAtom,
  Zap,
  zapsSearchAsyncAtomFamily,
  closeSecondaryToolbarMenuAtom,
} from "@zapier/canvas-state";

import { useEmitResourceConnectEvents } from "../../../hooks/useEmitResourceConnectEvents";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { useFocusInput } from "../../../hooks/useFocusInput";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { SelectedItem } from "../SelectedItem";
import { NodeToolbarBottom } from "../NodeToolbarBottom";
import { MenuWrapper } from "../MenuWrapper";

import styles from "./FloatingZapsTypeahead.module.css";

type Props = {
  hasZap?: boolean;
  isZapNotFound?: boolean;
  zapTitle: Zap["title"] | undefined;
};

export function FloatingZapsTypeahead({
  hasZap,
  zapTitle,
  isZapNotFound,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const updateNodeAndSave = useUpdateNode();
  const { emitEventOnZapConnect } = useEmitResourceConnectEvents();
  const setNewAssetIdentifier = useSetAtom(newAssetIdentifierAtom);
  const id = useNodeId()!;
  const createZapTimeSpentFromTimeSaved = useSetAtom(
    createZapTimeSpentFromTimeSavedAtom,
  );

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuAtom);

  const zapsLoadable = useAtomValue(
    zapsSearchAsyncAtomFamily({
      searchTerm: debouncedSearchTerm,
      showAllWhenEmpty: false,
    }),
  ) as Loadable<Zap[]>;
  const isLoading = zapsLoadable.state === "loading";
  const hasData = zapsLoadable.state === "hasData";
  const zaps = useMemo(
    () => (hasData ? zapsLoadable.data : []),
    [hasData, zapsLoadable],
  );

  useFocusInput(inputRef);

  const onInputChange = useCallback(
    (value: string) => setSearchTerm(value),
    [setSearchTerm],
  );

  const onSelectionChange = useCallback(
    (key: Key | null) => {
      if (key) {
        takeSnapshot();

        createZapTimeSpentFromTimeSaved(id, String(key));
        updateNodeAndSave({
          type: "zap",
          data: {
            zapId: key,
            isExpanded: false,
          },
        });

        setNewAssetIdentifier(`zap:${key}`);
        emitEventOnZapConnect(String(key));

        setSearchTerm("");
        closeSecondaryMenu();
      }
    },
    [
      takeSnapshot,
      createZapTimeSpentFromTimeSaved,
      id,
      updateNodeAndSave,
      setNewAssetIdentifier,
      emitEventOnZapConnect,
      closeSecondaryMenu,
    ],
  );

  const onRemoveZap = useCallback(() => {
    takeSnapshot();
    setNewAssetIdentifier(null);

    updateNodeAndSave({
      data: {
        zapId: undefined,
        isExpanded: undefined,
      },
    });
  }, [updateNodeAndSave, setNewAssetIdentifier, takeSnapshot]);

  return (
    <NodeToolbarBottom offset={15}>
      <MenuWrapper>
        {hasZap ? (
          <SelectedItem
            label={isZapNotFound ? "Zap not found" : zapTitle || "Untitled Zap"}
            icon={<Icon name="miscBoltAltFill" size={26} color="BrandOrange" />}
            clearSelection={onRemoveZap}
          />
        ) : (
          <>
            <div className={styles.floatingTypeaheadWrapper}>
              <Typeahead
                aria-label="Search Zaps"
                placeholder="Search Zaps"
                ref={inputRef}
                onInputChange={onInputChange}
                onSelectionChange={onSelectionChange}
                isBusy={isLoading}
                classNamePopOver={styles.popover}
                size="medium"
              >
                {zaps.map((zap) => {
                  return (
                    <Typeahead.Item
                      key={zap.id}
                      id={zap.id}
                      textValue={zap.title}
                      className={styles.floatingTypeaheadItem}
                      size="medium"
                    >
                      <Icon name="miscBoltAlt" size={22} color="BrandOrange" />
                      <div className={styles.truncateText}>
                        <Text type="SmallPrint2">
                          {zap.title ?? "Untitled Zap"}
                        </Text>
                      </div>
                    </Typeahead.Item>
                  );
                })}
              </Typeahead>
            </div>
          </>
        )}
      </MenuWrapper>
    </NodeToolbarBottom>
  );
}
